import { ReactElement } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ReactComponent as EcoDayIllustration } from '~/assets/img/ecoSimplified/eco-ao-longo-do-dia.svg';
import { ReactComponent as LowTemperatureIllustration } from '~/assets/img/ecoSimplified/protecao-temperatura-baixa.svg';
import { ReactComponent as StagePaceIllustration } from '~/assets/img/ecoSimplified/ritmo-dos-passos.svg';
import { DamWeekDayKey } from '~/providers/types/api-private';
import { colors } from '~/styles/colors';
import { mobileQuery, SidePanel } from '../../ds';
import { parseFormNumber } from '../../helpers';
import { DamEcoSimplifiedFormValues } from '../../types';
import { pickVentilationWindow } from '../../ventilationChart';
import { VentilationChart } from './VentilationChart';
import { ZoomableIllustration } from './ZoomableIllustration';

const JS_DAY_TO_KEY: DamWeekDayKey[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

const AdvancedRow = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 24px;
  padding-top: 22px;
  border-top: 1px solid ${colors.stroke['soft-200']};

  ${mobileQuery} {
    flex-direction: column;
    margin-top: 20px;
    padding-top: 18px;
  }
`;

/** "Modo ECO ao Longo de um Dia" (ilustração estática do design), ao lado do setpoint. */
export function EcoDayPanel(): ReactElement {
  const { t } = useTranslation();
  return (
    <SidePanel>
      <ZoomableIllustration ratio="1280 / 700" label={t('ecoSimplificadoIlustracaoDia')} layout={{ maxWidth: 520 }}>
        <EcoDayIllustration />
      </ZoomableIllustration>
    </SidePanel>
  );
}

/** "Proteção Contra Temperatura Baixa" e "Ritmo dos Passos e Inversão de Sentido", nas opções avançadas. */
export function AdvancedIllustrations(): ReactElement {
  const { t } = useTranslation();
  return (
    <AdvancedRow>
      <ZoomableIllustration ratio="1280 / 660" label={t('ecoSimplificadoIlustracaoLti')} layout={{ grow: true }}>
        <LowTemperatureIllustration />
      </ZoomableIllustration>
      <ZoomableIllustration ratio="1280 / 705" label={t('ecoSimplificadoIlustracaoRitmo')} layout={{ grow: true }}>
        <StagePaceIllustration />
      </ZoomableIllustration>
    </AdvancedRow>
  );
}

/** Gráfico de ventilação, que reage ao setpoint, aos minutos de ventilação e à programação de hoje. */
export function VentilationPanel(): ReactElement {
  const { t } = useTranslation();
  const { control } = useFormContext<DamEcoSimplifiedFormValues>();
  const [setpoint, ventStart, ventEnd, programming] = useWatch({ control, name: ['setpoint', 'ventStart', 'ventEnd', 'programming'] });
  const scheduleWindow = pickVentilationWindow(programming.week, JS_DAY_TO_KEY[new Date().getDay()]);
  const toMinutesValue = (value: string): number => Math.max(0, Math.round(parseFormNumber(value) ?? 0)) || 0;
  const setpointValue = parseFormNumber(setpoint);

  return (
    <SidePanel>
      <ZoomableIllustration ratio="700 / 352" label={t('ecoSimplificadoVentilacaoInicialFinal')} layout={{ maxWidth: 520 }}>
        <VentilationChart
          setpoint={setpointValue != null && Number.isFinite(setpointValue) ? setpointValue : null}
          ventilation={{ start: toMinutesValue(ventStart), end: toMinutesValue(ventEnd) }}
          schedule={scheduleWindow}
        />
      </ZoomableIllustration>
    </SidePanel>
  );
}
